// Shared Zod arg fragments for dashboard tile / sub-form
// schemas. SSOT for the recurring kwarg shapes so every tile and
// sub-form validates `:span` and `(col …)` slots byte-identically.
//
// Symbol-or-string acceptance for plain `z.string()` / `z.enum(...)`
// kwargs is handled centrally by `relaxSymbols` at the `parseOpCall`
// chokepoint (see schema/sym-codec.ts) — tiles/sub-forms write vanilla
// Zod; no per-fragment helper is needed here.
//
// Parser shapes (see normalize.ts): a vector `:span [4 2]` arrives as a
// JS number array; a `(col …)` ref as a raw OpCall AST node.

import { z } from 'zod';

import { SERIES_COLORS } from '#core/lib/formatters/colors.ts';

import { astArg } from '../../schema/ast-codec.ts';
import { type CategoryColors, SPAN_RANGE } from './value.ts';

/**
 * `:span [W H]` — a two-int vector, each component in the fixed
 * 12-column grid range. Output is a readonly tuple matching `Span`.
 */
const spanInt = z.number().int().min(SPAN_RANGE.min).max(SPAN_RANGE.max);
// No `.describe()`: the `:span` semantics are documented once in the
// tile scope's blurb — a per-tile kwarg row would repeat it on every
// tile entry of the DSL reference.
export const spanArg: z.ZodType<readonly [number, number]> = z
  .tuple([spanInt, spanInt])
  .transform(([w, h]) => [w, h] as const);

/**
 * A deferred column-ref arg (`:x (col w t)`, `:value (col w v)`). The
 * raw `(col …)` OpCall is preserved unevaluated here; the tile /
 * sub-form `apply` evaluates it via `ctx.evalNode` and narrows the
 * result with `expectColumnRef`. Branded as an AST codec so the
 * DSL-reference walker renders the signature type as the literal token
 * `ColRef` and `relaxSymbols` skips its opaque interior. "Column
 * references" prose documents the `(col …)` shape once.
 */
export const colRefArg = astArg('ColRef');

/**
 * `:colors {:category color …}` — an optional category→color override map for
 * `(donut …)` / `(treemap …)`. The parser yields the `{$map:[{k,v}]}` shape;
 * keys are raw category strings, values are chart color tokens. Transformed to
 * the `CategoryColors` record the renderer consumes; categories absent from the
 * map fall back to the renderer's order-stable palette assignment.
 */
const colorToken = z.union([z.enum(SERIES_COLORS), z.object({ $sym: z.enum(SERIES_COLORS) }).transform((s) => s.$sym)]);

export const categoryColorsArg = z
  .object({
    $map: z
      .array(z.object({ k: z.string().min(1), v: colorToken }))
      .min(1, { error: '`:colors` must declare at least one `{:category color}` entry' }),
  })
  .transform(({ $map }): CategoryColors => Object.fromEntries($map.map((e) => [e.k, e.v])));
